"use client"

import { sendGTMEvent } from "@next/third-parties/google"

import { PSEUDO_ID_COOKIE } from "@/platform/api/constants"
import { isAppMode } from "@/platform/app-mode/client"
import { claimBridgeEvent } from "@/platform/bridge/document-events"
import { createRequest } from "@/platform/bridge/envelope"
import { isAppBridgeAvailable, postToNativeHost } from "@/platform/bridge/use-native-bridge"
import type { MetaTrackParams } from "@/platform/observability/analytics/meta"
import { buildMetaGtmFields } from "@/platform/observability/analytics/meta"
import { nativeEventFor } from "@/platform/observability/analytics/native-events"
import { datadog } from "@/platform/observability/datadog/facade"
import { getSessionCookie } from "@/platform/session/auth/get-session-cookie-client"

import { onboardingFlowFields } from "./onboarding-flow"

import type { CustomEvent } from "./types/custom"
import type { GA4Event } from "./types/google"
import type { PdpEarlyTapEvent } from "./types/pdp"

type AnalyticsEvent = GA4Event | CustomEvent | PdpEarlyTapEvent
type AnalyticsEventName = AnalyticsEvent["event"]
type AnalyticsEventParams<E extends AnalyticsEventName> = Extract<AnalyticsEvent, { event: E }>["params"]

type EventingEnvelope = {
  [E in AnalyticsEventName]: {
    event: E
    params: AnalyticsEventParams<E>
    eventData: Record<string, unknown>
    internalParams: MetaTrackParams
  }
}[AnalyticsEventName]

interface AnalyticsAdapter {
  name: "ga4" | "gtm" | "datadog" | "native-bridge"
  shouldTrack?: (envelope: EventingEnvelope) => boolean
  track: (envelope: EventingEnvelope) => void
}

const SKIP_GTM_EVENTS = ["xp_inclusion"]

function splitInternalParams(params: Record<string, unknown>): {
  analyticsParams: Record<string, unknown>
  internalParams: MetaTrackParams
} {
  const { metaEventId, conversionId, conversionSurface, metaSignupChannel, ...analyticsParams } = params as Record<string, unknown> & MetaTrackParams

  return {
    analyticsParams,
    internalParams: {
      metaEventId,
      conversionId,
      conversionSurface,
      metaSignupChannel,
    },
  }
}

export function buildEventData(params: Record<string, unknown>): Record<string, unknown> {
  const pseudoId = getSessionCookie(PSEUDO_ID_COOKIE)

  return {
    storefront_session_id: pseudoId || undefined,
    nelo_pseudo_id: pseudoId || undefined,
    source: "store_front",
    ...onboardingFlowFields(),
    ...(isAppMode() ? { app_mode: true } : {}),
    ...params,
  }
}

declare global {
  interface Window {
    gtag?: (command: "event", name: string, params: Record<string, unknown>) => void
  }
}

const googleAnalyticsAdapter: AnalyticsAdapter = {
  name: "ga4",
  track: ({ event, eventData }) => {
    window.gtag?.("event", event, eventData)
  },
}

const googleTagManagerAdapter: AnalyticsAdapter = {
  name: "gtm",
  shouldTrack: ({ event }) => !SKIP_GTM_EVENTS.includes(event),
  track: ({ event, eventData, internalParams }) => {
    const metaFields = buildMetaGtmFields(event, {
      ...eventData,
      ...internalParams,
    })

    if (metaFields) {
      sendGTMEvent({ meta: null })
    }

    sendGTMEvent({ event, ...eventData, ...metaFields })
  },
}

const datadogAdapter: AnalyticsAdapter = {
  name: "datadog",
  track: ({ event, eventData }) => {
    datadog.addAction(event, eventData)
  },
}

const nativeBridgeAdapter: AnalyticsAdapter = {
  name: "native-bridge",
  shouldTrack: ({ event }) => isAppBridgeAvailable() && nativeEventFor(event, {}) !== null,
  track: ({ event, eventData, internalParams }) => {
    const forward = nativeEventFor(event, eventData, internalParams)
    if (forward === null) return

    const eventKey = typeof eventData.transaction_id === "string" ? `${event}:${eventData.transaction_id}` : undefined
    const id = claimBridgeEvent(eventKey)
    if (id !== null) postToNativeHost(createRequest("analytics.track", id, forward))
  },
}

const ANALYTICS_ADAPTERS: AnalyticsAdapter[] = [googleAnalyticsAdapter, googleTagManagerAdapter, datadogAdapter, nativeBridgeAdapter]

export function trackEvent<E extends AnalyticsEventName>(event: E, params: AnalyticsEventParams<E> = {} as AnalyticsEventParams<E>): void {
  const { analyticsParams, internalParams } = splitInternalParams(params as Record<string, unknown>)
  const envelope = {
    event,
    params,
    eventData: buildEventData(analyticsParams),
    internalParams,
  } as EventingEnvelope

  ANALYTICS_ADAPTERS.forEach((adapter) => {
    try {
      if (!adapter.shouldTrack || adapter.shouldTrack(envelope)) adapter.track(envelope)
    } catch {
      return
    }
  })
}
